import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dateUtil from '../../../utils/dateUtil';
import EmployeeService from '../../employees/services/EmployeeService';
import WorkAreaService from '../../workareas/services/WorkAreaService';
import ShiftService from '../services/ShiftService';
import httpService, { HttpError } from '../../../core/services/HttpService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import shiftErrorHandler from '../services/ShiftErrorHandler';
import { RangeMode } from '../../../utils/timeRangeUtils';
import LocalStorageService from '../../../core/services/LocalStorageService';
import { useUserRole } from '../../../hooks/useUserRole';
import { useShiftEnvironment } from './useShiftEnvironment';

export function useShiftData() {
  const { role, workAreaIds, isManager } = useUserRole();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      const validTabs = ['shifts', 'timeTrackings', 'absences', 'availabilities', 'dashboard'];
      if (tabParam && validTabs.includes(tabParam)) return tabParam;
      return localStorage.getItem('ws.activeTab') || 'shifts';
    } catch {
      return 'shifts';
    }
  });

  const [tableShifts, setTableShifts] = useState<any[]>([]);
  const [startDate, setStartDateState] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const weekParam = params.get('week');
    if (weekParam) {
      const parts = weekParam.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const parsed = new Date(year, month, day);
        if (!isNaN(parsed.getTime())) return dateUtil.getMonday(parsed);
      }
    }
    return dateUtil.getMonday(new Date());
  });

  const setStartDate = useCallback(
    (date: Date) => {
      const monday = dateUtil.getMonday(date);
      setStartDateState(monday);
      const year = monday.getFullYear();
      const month = String(monday.getMonth() + 1).padStart(2, '0');
      const day = String(monday.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const params = new URLSearchParams(window.location.search);
      params.set('week', dateStr);
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const [workAreaOptions, setWorkAreaOptions] = useState<any[]>([]);
  const [draggableWAList, setDraggableWAList] = useState<any[]>([]);
  const [hiddenWorkAreas, setHiddenWorkAreas] = useState<number[]>([]);

  const [selectedLocations, setSelectedLocations] = useState<number[]>(() => {
    try {
      const stored = localStorage.getItem('ws.selectedLocations');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
      const legacy = localStorage.getItem('ws.selectedLocation');
      if (legacy && legacy !== 'null') return [parseInt(legacy, 10)];
    } catch {}
    return [];
  });

  const [ttSelectedWAs, setTtSelectedWAs] = useState<number[]>(() => {
    try {
      const stored = localStorage.getItem('ws.ttSelectedWAs');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {}
    return [];
  });

  const [ttExportOpen, setTtExportOpen] = useState(false);
  const [ttImportOpen, setTtImportOpen] = useState(false);
  const [ttAddOpen, setTtAddOpen] = useState(false);

  const [absenceSelectedWAs, setAbsenceSelectedWAs] = useState<number[]>(() => {
    try {
      const stored = localStorage.getItem('ws.absenceSelectedWAs');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {}
    return [];
  });
  const [absenceAddOpen, setAbsenceAddOpen] = useState(false);
  const [absenceImportOpen, setAbsenceImportOpen] = useState(false);

  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [showAddShift, setShowAddShift] = useState(false);
  const [shiftData, setShiftData] = useState({
    start_time_date: '',
    start_time_time: '08:00',
    end_time_time: '13:00',
    employees_needed: 1,
    work_area: '',
    notes: '',
    weekdays: [] as number[],
    interval: 1,
    repeat_until: '',
    never_ends: false,
    crm_task: null as number | null,
    crm_task_title: '' as string,
  });

  useEffect(() => {
    if (searchParams.get('addShift') === 'true') {
      setShiftData(prev => ({
        ...prev,
        start_time_date: dateUtil.formatDate(new Date(), 'yyyy-MM-dd'),
      }));
      setShowAddShift(true);
      searchParams.delete('addShift');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const [showEditShift, setShowEditShift] = useState(false);
  const [editShiftData, setEditShiftData] = useState({
    shiftId: null as number | null,
    startDate: '',
    startTime: '',
    endTime: '',
    employeesNeeded: 1,
    workArea: '',
    notes: '',
    scope: 'this',
    crm_task: null as number | null,
    crm_task_title: '' as string,
  });

  const [allEmployees, setAllEmployees] = useState<any[]>([]);
  const [employeeContracts, setEmployeeContracts] = useState<any[]>([]);

  const [activeEmployeeId, setActiveEmployeeId] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem('ws.activeEmployeeId');
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  });
  const [activeEmployee, setActiveEmployee] = useState<any>(null);

  const [displayedTTs, setDisplayedTTs] = useState<any[]>([]);
  const [ttRangeStart, setTtRangeStart] = useState<Date | null>(null);
  const [ttRangeEnd, setTtRangeEnd] = useState<Date | null>(null);
  const [ttInitialRange] = useState<{ start: string; end: string } | undefined>(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const s = p.get('start');
      const e = p.get('end');
      if (s && e) return { start: s, end: e };
    } catch {}
    return undefined;
  });
  const [ttInitialMonth] = useState<Date | undefined>(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const m = p.get('month');
      if (m) {
        const [y, mo] = m.split('-').map(Number);
        if (y && mo) return new Date(y, mo - 1, 1);
      }
    } catch {}
    return undefined;
  });
  const [ttRangeMode, setTtRangeMode] = useState<RangeMode>('allTime');
  const [plannedHoursMap, setPlannedHoursMap] = useState<Record<number, number>>({});

  const [dragging, setDragging] = useState<{
    shiftId: number; startX: number; startY: number; x: number; y: number;
  } | null>(null);
  const [dropTarget, setDropTarget] = useState<{ date: string; waId: number } | null>(null);

  const [absModal, setAbsModal] = useState<{
    open: boolean; date?: string; type?: 'red' | 'orange'; anchor?: { top: number; left: number };
  }>({ open: false });

  const [showAutoAssignConfirm, setShowAutoAssignConfirm] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignmentLogs, setAssignmentLogs] = useState<string[]>([]);
  const addLog = (msg: string) => setAssignmentLogs(prev => [...prev, msg]);

  const [showCalendar, setShowCalendar] = useState(false);
  const calendarBtnRef = useRef<HTMLButtonElement>(null);
  const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0 });

  const [lastShiftDefaults, setLastShiftDefaults] = useState({
    start_time_time: '09:00',
    end_time_time: '17:00',
    work_area: '',
  });

  // ===== Environment: weather, events, absences, location sync =====
  const env = useShiftEnvironment({
    activeTab, startDate, draggableWAList, hiddenWorkAreas,
    selectedLocations, setHiddenWorkAreas,
  });

  // ===== Persistence effects =====
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      const empParam = params.get('emp');
      const validTabs = ['shifts', 'timeTrackings', 'absences', 'availabilities', 'dashboard'];
      if (tabParam && validTabs.includes(tabParam) && tabParam !== activeTab) {
        setActiveTab(tabParam);
      }
      if (empParam) {
        const id = Number(empParam);
        if (!Number.isNaN(id) && id !== activeEmployeeId) setActiveEmployeeId(id);
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try { localStorage.setItem('ws.activeTab', String(activeTab)); } catch {}
    try {
      const params = new URLSearchParams(window.location.search);
      params.set('tab', String(activeTab));
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    } catch {}
  }, [activeTab]);

  useEffect(() => {
    try { localStorage.setItem('ws.ttSelectedWAs', JSON.stringify(ttSelectedWAs)); } catch {}
  }, [ttSelectedWAs]);

  useEffect(() => {
    try { localStorage.setItem('ws.absenceSelectedWAs', JSON.stringify(absenceSelectedWAs)); } catch {}
  }, [absenceSelectedWAs]);

  useEffect(() => {
    try {
      if (activeEmployeeId === null || activeEmployeeId === undefined) {
        localStorage.removeItem('ws.activeEmployeeId');
      } else {
        localStorage.setItem('ws.activeEmployeeId', String(activeEmployeeId));
      }
      const params = new URLSearchParams(window.location.search);
      if (activeEmployeeId === null || activeEmployeeId === undefined) {
        params.delete('emp');
      } else {
        params.set('emp', String(activeEmployeeId));
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    } catch {}
  }, [activeEmployeeId]);

  useEffect(() => {
    localStorage.setItem('ws.selectedLocations', JSON.stringify(selectedLocations));
  }, [selectedLocations]);

  // ===== Planned hours for TT =====
  useEffect(() => {
    const loadPlanned = async () => {
      if (!ttRangeStart || !ttRangeEnd) return;
      try {
        const startStr = (ttRangeStart as Date).toISOString().split('T')[0];
        const days = Math.max(1, Math.round(((ttRangeEnd as Date).getTime() - (ttRangeStart as Date).getTime()) / 86400000));
        const options: any = { start: startStr, days };
        const weekly = await ShiftService.fetchWeeklyShifts(options);
        const map: Record<number, number> = {};
        const now = new Date();
        const plannedStart = new Date(Math.max(now.getTime(), (ttRangeStart as Date).getTime()));
        weekly.forEach((dayObj: any) => {
          if (!dayObj.shifts) return;
          dayObj.shifts.forEach((sh: any) => {
            const st = new Date(sh.start_time);
            const et = new Date(sh.end_time);
            if (!(st >= plannedStart && st < (ttRangeEnd as Date))) return;
            const diffH = Math.max(0, (et.getTime() - st.getTime()) / 3600000);
            if (sh.assigned_employees) {
              sh.assigned_employees.forEach((aEmp: any) => {
                const empId = aEmp.id;
                map[empId] = (map[empId] || 0) + diffH;
              });
            }
          });
        });
        setPlannedHoursMap(map);
      } catch (error) {
        console.error('load planned hours =>', error);
      }
    };
    loadPlanned();
  }, [ttRangeStart, ttRangeEnd]);

  // ===== Init: load employees + work areas =====
  useEffect(() => {
    const loadData = async () => {
      try {
        const workAreas = await WorkAreaService.fetchWorkAreas();
        setWorkAreaOptions(workAreas);
        const savedOrder = LocalStorageService.getWaOrder();
        if (savedOrder.length) {
          const sorted = [...workAreas].sort(
            (a: any, b: any) => savedOrder.indexOf(a.id) - savedOrder.indexOf(b.id)
          );
          setDraggableWAList(sorted);
        } else {
          setDraggableWAList(workAreas);
        }
      } catch (error) {
        console.error('Error WA =>', error);
        if (error instanceof HttpError) globalErrorHandler.handle(error);
      }

      try {
        const employees = await EmployeeService.fetchAllEmployees();
        setAllEmployees(Array.isArray(employees) ? employees : []);
      } catch (error) {
        console.error('Error Emps =>', error);
        if (error instanceof HttpError) globalErrorHandler.handle(error);
      }

      try {
        const res = await httpService.get<any>('/api/employee-contracts/');
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setEmployeeContracts(data);
      } catch {}
    };
    loadData();
  }, []);

  useEffect(() => {
    if (isManager && workAreaIds.length > 0 && draggableWAList.length > 0) {
      const otherWAs = draggableWAList.filter((wa: any) => !workAreaIds.includes(wa.id)).map((wa: any) => wa.id);
      setHiddenWorkAreas(otherWAs);
    }
  }, [isManager, workAreaIds, draggableWAList]);

  useEffect(() => {
    if (workAreaOptions.length > 0 && ttSelectedWAs.length === 0) {
      setTtSelectedWAs(workAreaOptions.map((wa: any) => wa.id));
    }
  }, [workAreaOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Shift fetch =====
  const fetchTableShifts = useCallback(async () => {
    if (activeTab !== 'shifts') return Promise.resolve([]);
    try {
      const isoDate = dateUtil.getMondayDateString(startDate);
      const visibleWAs = draggableWAList
        .filter((wa: any) => !hiddenWorkAreas.includes(wa.id))
        .map((wa: any) => wa.id);
      const options: any = {
        start: isoDate,
        days: 7,
        work_area: hiddenWorkAreas.length > 0 ? visibleWAs.join(',') : undefined,
      };
      const shifts = await ShiftService.fetchWeeklyShifts(options);
      setTableShifts(shifts);
      return shifts;
    } catch (error) {
      console.error('Error SHIFT-Fetch =>', error);
      if (error instanceof HttpError) {
        if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
      }
      return [];
    }
  }, [activeTab, startDate, hiddenWorkAreas, draggableWAList]);

  useEffect(() => {
    fetchTableShifts();
  }, [activeTab, startDate, hiddenWorkAreas, fetchTableShifts]);

  // ===== Computed values =====
  const getWAHours = useCallback(
    (waId: number) => {
      let total = 0;
      let assigned = 0;
      (tableShifts as any[]).forEach((dayObj: any) => {
        (dayObj.shifts || []).forEach((shift: any) => {
          if (shift.work_area === waId) {
            const start: any = new Date(shift.start_time);
            const end: any = new Date(shift.end_time);
            const hours: any = (end - start) / 3600000;
            total += hours * (shift.employees_needed || 0);
            assigned += hours * (shift.assigned_employees?.length || 0);
          }
        });
      });
      return { total: Math.round(total), assigned: Math.round(assigned) };
    },
    [tableShifts]
  );

  const getTotalHours = useCallback(() => {
    let totalNeeded = 0;
    (tableShifts as any[]).forEach((dayObj: any) => {
      (dayObj.shifts || []).forEach((shift: any) => {
        const start: any = new Date(shift.start_time);
        const end: any = new Date(shift.end_time);
        const hours: any = (end - start) / 3600000;
        totalNeeded += hours * (shift.employees_needed || 0);
      });
    });
    let totalAvailable = 0;
    allEmployees.forEach((emp: any) => {
      const empHours = emp.contract_hours_per_week ?? emp.min_hours ?? 10;
      totalAvailable += empHours;
    });
    return { needed: Math.round(totalNeeded), available: Math.round(totalAvailable) };
  }, [tableShifts, allEmployees]);

  const handleTTsRangeChange = (start: any, end: any, filteredList: any) => {
    if (start !== ttRangeStart) setTtRangeStart(start);
    if (end !== ttRangeEnd) setTtRangeEnd(end);
    if (JSON.stringify(filteredList) !== JSON.stringify(displayedTTs)) setDisplayedTTs(filteredList || []);
  };

  return {
    role, workAreaIds, isManager,
    activeTab, setActiveTab,
    tableShifts, setTableShifts,
    startDate, setStartDate,
    workAreaOptions,
    draggableWAList, setDraggableWAList,
    hiddenWorkAreas, setHiddenWorkAreas,
    selectedLocations, setSelectedLocations,
    ttSelectedWAs, setTtSelectedWAs,
    ttExportOpen, setTtExportOpen,
    ttImportOpen, setTtImportOpen,
    ttAddOpen, setTtAddOpen,
    absenceSelectedWAs, setAbsenceSelectedWAs,
    absenceAddOpen, setAbsenceAddOpen,
    absenceImportOpen, setAbsenceImportOpen,
    showClearAllModal, setShowClearAllModal,
    showAddShift, setShowAddShift,
    shiftData, setShiftData,
    showEditShift, setShowEditShift,
    editShiftData, setEditShiftData,
    allEmployees, employeeContracts,
    activeEmployeeId, setActiveEmployeeId,
    activeEmployee, setActiveEmployee,
    displayedTTs,
    ttRangeStart, ttRangeEnd,
    ttInitialRange, ttInitialMonth, ttRangeMode,
    plannedHoursMap,
    dragging, setDragging,
    dropTarget, setDropTarget,
    absModal, setAbsModal,
    showAutoAssignConfirm, setShowAutoAssignConfirm,
    isAssigning, setIsAssigning,
    assignmentLogs, setAssignmentLogs, addLog,
    showCalendar, setShowCalendar,
    calendarBtnRef, calendarPos, setCalendarPos,
    lastShiftDefaults, setLastShiftDefaults,
    fetchTableShifts,
    getWAHours, getTotalHours,
    handleTTsRangeChange,
    // From useShiftEnvironment
    weatherData: env.weatherData,
    weatherByLocation: env.weatherByLocation,
    uniqueLocations: env.uniqueLocations,
    weekEvents: env.weekEvents,
    weekAbsences: env.weekAbsences,
    allAbsencesForSidebar: env.allAbsencesForSidebar,
    countsByDate: env.countsByDate,
  };
}
